import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../ui/dropdown-menu';
import {
  Activity, Send, Eye, MessageSquare, CheckCircle2, Radio,
  Upload, Database, Plus, Search, ArrowUpDown, Pencil, Trash2, Paperclip,
} from 'lucide-react';

// V14.10.7 — read-state tracker for inject responses. Stored per exercise +
// user in localStorage so the "new since you last looked" blink dot persists
// across tab refreshes but doesn't bleed across exercises.
const READ_KEY = (exerciseId) => `ems_inject_responses_seen_${exerciseId || 'global'}`;
function loadSeenCounts(exerciseId) {
  try { return JSON.parse(localStorage.getItem(READ_KEY(exerciseId)) || '{}'); }
  catch { return {}; }
}
function saveSeenCounts(exerciseId, counts) {
  try { localStorage.setItem(READ_KEY(exerciseId), JSON.stringify(counts)); } catch {}
}

export function InjectsTab({
  filteredInjects, injects, responses = [], exerciseId,
  searchFilter, setSearchFilter,
  recipientFilter, setRecipientFilter,
  statusFilter, setStatusFilter,
  sortOrder, setSortOrder,
  isController, isObserver, mpBridgeStatus, publishingInject,
  setCsvImportOpen, setInjectDbPickerOpen, setEditingInjectId, resetInjectForm, setInjectDialogOpen,
  handleTriggerInject, handleCompleteInject, handleChangeInjectStatus,
  setViewInject, setInjectViewOpen, openResponseDialog, openEditInject,
  setMpPublishInjectId, setMpPublishPlatform, setMpPublishConfirmOpen,
  getRecipientLabel, getInjectIdLabel, getDisplayTime, getPriorityClass,
  handleDeleteInject, exerciseStatus,
}) {
  // ── Response & attachment counts per inject ──────────────────────────
  const stats = useMemo(() => {
    const byId = {};
    for (const r of responses) {
      const id = r.inject_id;
      if (!id) continue;
      if (!byId[id]) byId[id] = { responses: 0, attachments: 0 };
      byId[id].responses += 1;
      byId[id].attachments += (r.attachments || []).length;
    }
    return byId;
  }, [responses]);

  const [seen, setSeen] = useState(() => loadSeenCounts(exerciseId));
  // Whenever the user opens the response/notes dialog for an inject, mark
  // its current count as "seen" so the blink dot stops blinking.
  const markSeen = (injectId) => {
    const current = stats[injectId]?.responses || 0;
    if (seen[injectId] === current) return;
    const next = { ...seen, [injectId]: current };
    setSeen(next);
    saveSeenCounts(exerciseId, next);
  };
  // Also persist "seen" whenever counts decrease (e.g. response deleted)
  useEffect(() => {
    let changed = false;
    const next = { ...seen };
    for (const [id, s] of Object.entries(seen)) {
      const cur = stats[id]?.responses || 0;
      if (cur < s) { next[id] = cur; changed = true; }
    }
    if (changed) { setSeen(next); saveSeenCounts(exerciseId, next); }
    // eslint-disable-next-line
  }, [stats]);

  const hasNew = (injectId) => (stats[injectId]?.responses || 0) > (seen[injectId] || 0);
  return (
    <div className="space-y-4">
      {/* Header: search, filters, actions */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input className="pl-7 h-8 text-xs" placeholder="Search injects..." value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} data-testid="inject-search-input" />
        </div>
        <Select value={recipientFilter} onValueChange={setRecipientFilter}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="inject-recipient-filter"><SelectValue placeholder="Recipient" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All Recipients</SelectItem>
            {[...new Set(injects.map(i => getRecipientLabel(i)).filter(r => r && r !== '-'))].sort().map(r => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="inject-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="ongoing">Ongoing</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 text-xs"><ArrowUpDown className="w-3 h-3 mr-1" />Sort</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setSortOrder('newest')}>Newest First</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSortOrder('oldest')}>Oldest First</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSortOrder('priority')}>By Priority</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSortOrder('inject_time')}>By Inject Time</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {isController() && (
          <>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setCsvImportOpen(true)} data-testid="import-csv-btn"><Upload className="w-4 h-4 mr-1" />Import CSV</Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setInjectDbPickerOpen(true)} data-testid="select-from-db-btn"><Database className="w-4 h-4 mr-1" />From Library</Button>
            <Button size="sm" className="h-8 text-xs" onClick={() => { setEditingInjectId(null); resetInjectForm(); setInjectDialogOpen(true); }} data-testid="create-inject-btn"><Plus className="w-4 h-4 mr-1" />New Inject</Button>
          </>
        )}
      </div>

      {/* Inject Table */}
      {filteredInjects.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Activity className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">{searchFilter || statusFilter !== 'all' ? 'No matching injects' : 'No injects yet'}</p>
            {isController() && !searchFilter && statusFilter === 'all' && (
              <Button onClick={() => { setEditingInjectId(null); resetInjectForm(); setInjectDialogOpen(true); }} className="mt-4">Create First Inject</Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg overflow-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          <table className="w-full text-xs">
            <thead className="bg-muted/50 sticky top-0 z-10">
              <tr>
                <th className="text-left p-2 font-medium text-muted-foreground w-[50px]">ID</th>
                <th className="text-left p-2 font-medium text-muted-foreground w-[100px]">Status</th>
                <th className="text-left p-2 font-medium text-muted-foreground w-[70px]">Priority</th>
                <th className="text-left p-2 font-medium text-muted-foreground w-[100px]">Recipient</th>
                <th className="text-left p-2 font-medium text-muted-foreground">Title</th>
                <th className="text-left p-2 font-medium text-muted-foreground w-[90px]">Time</th>
                <th className="text-right p-2 font-medium text-muted-foreground w-[220px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredInjects.map((inject) => {
                const isDraft = inject.status === 'draft' || inject.status === 'pending';
                const isOngoing = inject.status === 'ongoing';
                const isCompleted = inject.status === 'completed';
                const rowColor = isCompleted ? 'bg-green-50 dark:bg-green-950/20' : isOngoing ? 'bg-blue-50 dark:bg-blue-950/20' : '';
                const displayStatus = inject.status === 'pending' ? 'draft' : inject.status;
                return (
                  <tr key={inject.inject_id} className={`hover:bg-muted/30 transition-colors ${rowColor}`} data-testid={`inject-row-${inject.inject_id}`}>
                    <td className="p-2 font-mono text-muted-foreground">{getInjectIdLabel(inject)}</td>
                    <td className="p-2">
                      <div className="flex flex-col gap-0.5">
                        {isController() ? (
                          <Select value={displayStatus} onValueChange={(v) => {
                            if (v === 'ongoing' && isDraft) handleTriggerInject(inject.inject_id);
                            else if (v === 'completed' && isOngoing) handleCompleteInject(inject.inject_id);
                            else handleChangeInjectStatus(inject.inject_id, v);
                          }}>
                            <SelectTrigger className="h-6 text-[10px] w-[90px] px-1" data-testid={`inject-status-${inject.inject_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="draft">Draft</SelectItem>
                              <SelectItem value="ongoing">Ongoing</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className={`capitalize text-[10px] ${isCompleted ? 'text-green-600 border-green-300' : isOngoing ? 'text-blue-600 border-blue-300' : 'text-muted-foreground'}`}>{displayStatus}</Badge>
                        )}
                        {/* V14.10.8 — "by &lt;Name&gt;" hint visible on ongoing/completed rows */}
                        {(isOngoing || isCompleted) && (inject.triggered_by_name || inject.created_by_name) && (
                          <span
                            className="text-[9px] text-muted-foreground truncate max-w-[100px]"
                            title={`Triggered by ${inject.triggered_by_name || inject.created_by_name}`}
                            data-testid={`inject-triggered-by-${inject.inject_id}`}
                          >
                            by {inject.triggered_by_name || inject.created_by_name}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-2"><Badge variant="outline" className={`${getPriorityClass(inject.priority)} border capitalize text-[10px]`}>{inject.priority}</Badge></td>
                    <td className="p-2 text-muted-foreground truncate max-w-[100px]">{getRecipientLabel(inject)}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <button className="text-left hover:text-primary transition-colors line-clamp-1" onClick={() => { setViewInject(inject); setInjectViewOpen(true); }} title={inject.title}>{inject.title || '-'}</button>
                        {/* V14.10.7 — response + attachment count badges */}
                        {(stats[inject.inject_id]?.responses || 0) > 0 && (
                          <span
                            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${
                              hasNew(inject.inject_id)
                                ? 'bg-amber-500/15 text-amber-700 border border-amber-500/30 animate-pulse ring-1 ring-amber-400/40'
                                : 'bg-muted text-muted-foreground border border-border'
                            }`}
                            title={`${stats[inject.inject_id].responses} response${stats[inject.inject_id].responses === 1 ? '' : 's'}${hasNew(inject.inject_id) ? ' (new — click Notes to mark as read)' : ''}`}
                            data-testid={`inject-response-count-${inject.inject_id}`}
                          >
                            <MessageSquare className="w-2.5 h-2.5" />
                            {stats[inject.inject_id].responses}
                          </span>
                        )}
                        {(stats[inject.inject_id]?.attachments || 0) > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium shrink-0 bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/30"
                            title={`${stats[inject.inject_id].attachments} attachment${stats[inject.inject_id].attachments === 1 ? '' : 's'}`}
                            data-testid={`inject-attachment-count-${inject.inject_id}`}
                          >
                            <Paperclip className="w-2.5 h-2.5" />
                            {stats[inject.inject_id].attachments}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-2 text-muted-foreground">{getDisplayTime(inject)}</td>
                    <td className="p-2">
                      <div className="flex items-center justify-end gap-1">
                        {isController() && isDraft && (
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-green-600 hover:text-green-700 hover:bg-green-50" onClick={() => handleTriggerInject(inject.inject_id)} data-testid={`inject-play-${inject.inject_id}`}><Send className="w-3 h-3 mr-0.5" />Play</Button>
                        )}
                        {isController() && isOngoing && (
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-green-600 hover:text-green-700 hover:bg-green-50" onClick={() => handleCompleteInject(inject.inject_id)} data-testid={`inject-complete-${inject.inject_id}`}><CheckCircle2 className="w-3 h-3 mr-0.5" />Complete</Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => { markSeen(inject.inject_id); setViewInject(inject); setInjectViewOpen(true); }} data-testid={`inject-view-${inject.inject_id}`}><Eye className="w-3 h-3 mr-0.5" />View</Button>
                        {isOngoing && (
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => { markSeen(inject.inject_id); openResponseDialog(inject); }} data-testid={`inject-notes-${inject.inject_id}`}><MessageSquare className="w-3 h-3 mr-0.5" />Notes</Button>
                        )}
                        {isController() && mpBridgeStatus?.linked && !inject.metadata?.mp_post_id && (isDraft || isOngoing) && (
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-blue-600" onClick={() => { setMpPublishInjectId(inject.inject_id); setMpPublishPlatform(''); setMpPublishConfirmOpen(true); }} disabled={publishingInject === inject.inject_id} data-testid={`mp-publish-inject-${inject.inject_id}`}><Radio className="w-3 h-3 mr-0.5" />{publishingInject === inject.inject_id ? '...' : 'Media'}</Button>
                        )}
                        {isController() && (
                          <Button
                            size="sm" variant="ghost"
                            className="h-6 px-1.5 text-[10px]"
                            onClick={() => openEditInject && openEditInject(inject)}
                            data-testid={`inject-edit-${inject.inject_id}`}
                            title="Edit inject"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                        )}
                        {isController() && handleDeleteInject && (
                          <Button
                            size="sm" variant="ghost"
                            className="h-6 px-1.5 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                            onClick={() => {
                              const isActive = exerciseStatus === 'active';
                              const msg = isActive
                                ? `⚠ The exercise is RUNNING. Are you absolutely sure you want to remove "${inject.title || inject.inject_id}" from this exercise? This cannot be undone.`
                                : `Remove "${inject.title || inject.inject_id}" from this exercise? This cannot be undone.`;
                              if (window.confirm(msg)) {
                                handleDeleteInject(inject.inject_id);
                              }
                            }}
                            data-testid={`inject-delete-${inject.inject_id}`}
                            title="Remove from exercise"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
